/**
 * Automation HTTP service (PX-W6.5) — the org-scoped scheduler reads + the
 * project-scoped context-index pair.
 *
 * ⚠ There is deliberately NO enable/disable verb: no route exists, because
 * a job's switch lives in `px.scheduler.jobs.<key>.enabled` and turning one
 * on is a reviewed deployment act (INV-6).
 *
 * ⚠ `runJob` passes `?projectId=` ONLY when a project is selected — the
 * route default is blank (org-wide) and a named project additionally runs
 * that project's WRITE gate.
 */

import { apiClient } from '@/services'

import type { ContextIndexRunView, JobRunResult, ProjectOption, SchedulerJobView } from '../types'

export const automationService = {
  /**
   * The picker's private `{id, name}` projection — this feature's own
   * service, never a shared `features/projects` file (plan Decision 12).
   */
  listProjects(): Promise<ProjectOption[]> {
    return apiClient.get<ProjectOption[]>('/{orgId}/projects')
  },

  /** Every registered job's four recorded facts. */
  listJobs(): Promise<SchedulerJobView[]> {
    return apiClient.get<SchedulerJobView[]>('/{orgId}/scheduler/jobs')
  },

  /** Run one job now; the response is the job's own outcome, verbatim. */
  runJob(jobKey: string, projectId: string | null): Promise<JobRunResult> {
    return apiClient.post<JobRunResult>(
      `/{orgId}/scheduler/jobs/${encodeURIComponent(jobKey)}/run`,
      undefined,
      projectId === null || projectId === '' ? undefined : { params: { projectId } }
    )
  },

  /** The project's last context-index run report. */
  contextIndexStatus(projectId: string): Promise<ContextIndexRunView> {
    return apiClient.get<ContextIndexRunView>(
      `/{orgId}/projects/${encodeURIComponent(projectId)}/context-index/status`
    )
  },

  /** Trigger a manual context-index run for the project. */
  runContextIndex(projectId: string): Promise<ContextIndexRunView> {
    return apiClient.post<ContextIndexRunView>(
      `/{orgId}/projects/${encodeURIComponent(projectId)}/context-index/run`
    )
  }
}
